import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * One broken screen should not take the whole application with it.
 *
 * React unmounts the entire tree when a render throws, and the result is a white
 * page: no menu, no way back, nothing on screen to say what happened. The shell
 * around this boundary survives, so the person can walk to another section
 * instead of reloading and losing where they were.
 *
 * A class, because this is the one thing hooks cannot do.
 */
interface Props {
  children: ReactNode;
  /** Changing this clears the error — used to recover on navigating away. */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(previous: Props): void {
    if (this.state.error && previous.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // The console is where a developer will look, and the stack is worth more
    // than anything this screen could show a warehouse keeper.
    console.error('screen failed', error, info.componentStack);
  }

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return <CrashNotice message={this.state.error.message} />;
  }
}

function CrashNotice({ message }: { message: string }) {
  const { t } = useTranslation();
  return (
    <div className="crash card">
      <h2>{t('errors.crashed')}</h2>
      <p className="hint">{t('errors.crashedHint')}</p>
      <p className="components__code">{message}</p>
      <button type="button" className="btn" onClick={() => window.location.reload()}>
        {t('errors.reload')}
      </button>
    </div>
  );
}
